<div class="printable-preview-root" [class.is-dialog]="isDialogMode">
    <header class="printable-preview-bar">
        <div class="printable-preview-meta">
            <span class="printable-preview-title">
                Preview :
                <span class="printable-preview-query">{{ queryValue || '—' }}</span>
            </span>
        </div>
        <div class="printable-preview-meta-right">
            <span class="printable-preview-spec">A4 · {{ a4Dpi }} DPI · {{ a4WidthPx }}×{{ a4HeightPx }}px</span>
            <span class="printable-preview-pages" *ngIf="pagesReady && !noRecords">
                {{ pages.length }} page{{ pages.length === 1 ? '' : 's' }}
            </span>
            <button type="button" class="printable-download-pdf-btn" *ngIf="pagesReady && !noRecords && pages.length" (click)="downloadPdf()">
                <i class="fas fa-file-pdf" aria-hidden="true"></i>
                Save as PDF
            </button>
            <button type="button" class="printable-close-btn" *ngIf="isDialogMode" (click)="closePreview()" aria-label="Close preview">
                <i class="fas fa-times" aria-hidden="true"></i>
                Close
            </button>
        </div>
    </header>
    <p class="printable-print-hint" *ngIf="pagesReady && !noRecords && pages.length">
        Click any text to edit. Then <strong>Save as PDF</strong>
    </p>

    <div class="text-light printable-empty" *ngIf="noRecords">No Records Found</div>

    <div *ngIf="isLoadingResults || isPaginating" class="loading-container text-center my-3">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-2 text-main-color">
            {{ isLoadingResults ? 'Fetching results, please wait...' : 'Arranging A4 pages...' }}
        </p>
    </div>

    <!-- Off-screen measure host: fixed WIDTH only so heights are not clipped -->
    <div class="a4-measure-host" aria-hidden="true" *ngIf="measureBlocks.length && !pagesReady">
        <div class="a4-measure-sheet" [ngStyle]="measureSheetStyle">
            <div class="a4-block" *ngFor="let block of measureBlocks; trackBy: trackByBlockKey" [attr.data-block-key]="block.key" #measureBlock>
                <ng-container [ngTemplateOutlet]="blockTpl" [ngTemplateOutletContext]="{ $implicit: block }"></ng-container>
            </div>
        </div>
    </div>

    <div class="a4-preview-stage" *ngIf="pagesReady && !noRecords && pages.length">
        <article class="a4-page-sheet a4-preview-sheet" *ngFor="let page of pages; trackBy: trackByPageNumber" [class.is-growing]="page.allowGrow" [ngStyle]="getPageSheetStyle(page)" [attr.aria-label]="'Page ' + page.pageNumber + ' of ' + pages.length">
            <div class="a4-page-body is-editable" contenteditable="true" spellcheck="true" (click)="onEditableClick($event)">
                <div class="a4-block" *ngFor="let block of page.blocks; trackBy: trackByBlockKey">
                    <ng-container [ngTemplateOutlet]="blockTpl" [ngTemplateOutletContext]="{ $implicit: block }"></ng-container>
                </div>
            </div>
            <footer class="a4-page-footer is-editable" contenteditable="true" spellcheck="true">
                <span>{{ reportTitle }} · {{ queryValue || '—' }}</span>
                <span class="a4-page-footer-meta">
                    <span *ngIf="generatedBy">
                        Generated by {{ generatedBy }}
                        <ng-container *ngIf="generatedAt"> · {{ generatedAt }}</ng-container>
                    </span>
                    <span>Page {{ page.pageNumber }} / {{ pages.length }}</span>
                </span>
            </footer>
        </article>
    </div>
</div>

<ng-template #blockTpl let-block>
    <ng-container [ngSwitch]="block.type">
        <div class="printable-report-header hero-card" *ngSwitchCase="'header'">
            <div class="hero-left">
                <img src="favicon.png" alt="App logo" class="printable-report-logo" />
                <div class="hero-meta">
                    <span class="hero-chip">{{ reportTitle }}</span>
                    <h1 class="hero-title">{{ queryValue || '—' }}</h1>
                    <p class="hero-subtitle">{{ reportSubtitle }}</p>
                </div>
            </div>
        </div>

        <section class="result-status-cards" [class.gst-status-cards]="isRegistryPrintable" [class.din-status-cards]="isDinPrintable" *ngSwitchCase="'status'" aria-label="Search result summary">
            <div class="result-status-card is-photos printable-status-card" *ngIf="!isRegistryPrintable">
                <span class="result-status-accent" aria-hidden="true"></span>
                <span class="result-status-icon" aria-hidden="true"><i class="bi bi-image"></i></span>
                <span class="result-status-count">{{ photosCount }}</span>
                <span class="result-status-label">Photos</span>
            </div>
            <div class="result-status-card is-names printable-status-card">
                <span class="result-status-accent" aria-hidden="true"></span>
                <span class="result-status-icon" aria-hidden="true"><i class="bi bi-person"></i></span>
                <span class="result-status-count">{{ namesCount }}</span>
                <span class="result-status-label">Names</span>
            </div>
            <div class="result-status-card is-emails printable-status-card">
                <span class="result-status-accent" aria-hidden="true"></span>
                <span class="result-status-icon" aria-hidden="true"><i class="bi bi-envelope"></i></span>
                <span class="result-status-count">{{ emailsCount }}</span>
                <span class="result-status-label">Emails</span>
            </div>
            <div class="result-status-card is-email-hints printable-status-card" *ngIf="!isRegistryPrintable && emailHintsCount">
                <span class="result-status-accent" aria-hidden="true"></span>
                <span class="result-status-icon" aria-hidden="true"><i class="bi bi-at"></i></span>
                <span class="result-status-count">{{ emailHintsCount }}</span>
                <span class="result-status-label">Email Hints</span>
            </div>
        </section>

        <section class="image-gallery-section" *ngSwitchCase="'photos'" [class.has-name-fill]="hasPhotoNameFill(block)">
            <div class="gallery-heading collage-heading" *ngIf="block.showPhotosHeading && !hasPhotoNameFill(block)">
                <div class="gallery-heading-main">
                    <i class="fas fa-image" aria-hidden="true"></i>
                    <h2>Recovered Photos</h2>
                    <span class="identity-summary-confirmed">{{ photosCount }} confirmed</span>
                </div>
            </div>
            <div class="photos-names-combo" *ngIf="hasPhotoNameFill(block)">
                <div class="printable-photos-grid is-partial-row" [ngStyle]="getPhotosGridStyle(block)">
                    <figure class="printable-photo-item" *ngFor="let image of block.images">
                        <img [src]="image.src" [alt]="image.name || image.platform" (error)="onIconError($event)" />
                        <figcaption *ngIf="image.platform">{{ image.platform }}</figcaption>
                    </figure>
                </div>
                <section class="identity-summary-panel names-summary-card photos-names-fill" aria-label="Names">
                    <header class="identity-summary-header">
                        <div class="identity-summary-heading">
                            <i class="fas fa-user" aria-hidden="true"></i>
                            <span class="identity-summary-title">Names</span>
                            <span class="identity-summary-confirmed">{{ aggregatedNames.length }} confirmed</span>
                        </div>
                    </header>
                    <div class="identity-item-grid photos-fill-names-grid" [ngStyle]="getPhotoFillNamesGridStyle(block)">
                        <article class="identity-item-card" *ngFor="let name of block.fillNames">
                            <div class="identity-item-body">
                                <div class="identity-item-top">
                                    <span class="identity-check" aria-hidden="true"><i class="bi bi-check-circle"></i></span>
                                    <span class="identity-item-value" [attr.title]="name">{{ name }}</span>
                                </div>
                            </div>
                        </article>
                    </div>
                </section>
            </div>
            <div class="printable-photos-grid" *ngIf="!hasPhotoNameFill(block)">
                <figure class="printable-photo-item" *ngFor="let image of block.images">
                    <img [src]="image.src" [alt]="image.name || image.platform" (error)="onIconError($event)" />
                    <figcaption *ngIf="image.platform">{{ image.platform }}</figcaption>
                </figure>
            </div>
        </section>

        <section class="original-photos-section" *ngSwitchCase="'originalPhoto'">
            <div class="gallery-heading collage-heading" *ngIf="block.showOriginalPhotosHeading">
                <div class="gallery-heading-main">
                    <i class="fas fa-image" aria-hidden="true"></i>
                    <h2>Original Photos</h2>
                    <span class="identity-summary-confirmed">{{ photosCount }} confirmed</span>
                </div>
            </div>
            <figure class="original-photo-item" *ngIf="block.image as image">
                <img [src]="image.src" [alt]="image.name || image.platform" (error)="onIconError($event)" />
                <figcaption *ngIf="image.platform || image.name">
                    <span *ngIf="image.platform">{{ image.platform }}</span>
                    <span *ngIf="image.platform && image.name"> · </span>
                    <span *ngIf="image.name">{{ image.name }}</span>
                </figcaption>
            </figure>
        </section>

        <section class="identity-summary-panel names-summary-card" *ngSwitchCase="'names'">
            <header class="identity-summary-header" *ngIf="block.showNamesHeading">
                <div class="identity-summary-heading">
                    <i class="fas fa-user" aria-hidden="true"></i>
                    <span class="identity-summary-title">Names</span>
                    <span class="identity-summary-confirmed">{{ aggregatedNames.length }} confirmed</span>
                </div>
            </header>
            <div class="identity-item-grid">
                <article class="identity-item-card" *ngFor="let name of block.names">
                    <div class="identity-item-body">
                        <div class="identity-item-top">
                            <span class="identity-check" aria-hidden="true"><i class="bi bi-check-circle"></i></span>
                            <span class="identity-item-value" [attr.title]="name">{{ name }}</span>
                        </div>
                    </div>
                </article>
            </div>
        </section>

        <section class="identity-summary-panel emails-summary-card" *ngSwitchCase="'emails'">
            <header class="identity-summary-header" *ngIf="block.showEmailsHeading">
                <div class="identity-summary-heading">
                    <i class="fas fa-envelope" aria-hidden="true"></i>
                    <span class="identity-summary-title">Email addresses</span>
                    <span class="identity-summary-confirmed">{{ aggregatedEmails.length }} confirmed</span>
                </div>
            </header>
            <div class="identity-item-grid">
                <article class="identity-item-card" *ngFor="let email of block.emails">
                    <div class="identity-item-body">
                        <div class="identity-item-top">
                            <span class="identity-check" aria-hidden="true"><i class="bi bi-check-circle"></i></span>
                            <span class="identity-item-value" [attr.title]="email">{{ email }}</span>
                        </div>
                    </div>
                </article>
            </div>
        </section>

        <section class="identity-summary-panel emails-summary-card" *ngSwitchCase="'emailHints'">
            <header class="identity-summary-header">
                <div class="identity-summary-heading">
                    <i class="fas fa-at" aria-hidden="true"></i>
                    <span class="identity-summary-title">Email Hints</span>
                    <span class="identity-summary-confirmed">{{ aggregatedEmailHints.length }} confirmed</span>
                </div>
            </header>
            <div class="identity-item-grid">
                <article class="identity-item-card" *ngFor="let emailHint of aggregatedEmailHints">
                    <div class="identity-item-body">
                        <div class="identity-item-top">
                            <span class="identity-check" aria-hidden="true"><i class="bi bi-at"></i></span>
                            <span class="identity-item-value" [attr.title]="emailHint">{{ emailHint }}</span>
                        </div>
                    </div>
                </article>
            </div>
        </section>

        <!-- Single platform (measure mode at real column width) -->
        <div class="source-card-grid abacus-card-grid measure-single-platform" [class.gst-measure-full]="isRegistryPrintable" [class.din-measure-full]="isDinPrintable" *ngSwitchCase="'platform'" [style.width.px]="platformColumnWidthPx">
            <ng-container *ngTemplateOutlet="platformCardTpl; context: { $implicit: block.platform }"></ng-container>
        </div>

        <!-- Multiple platforms: explicit columns matching packer (no CSS column clipping) -->
        <div class="a4-platform-columns" *ngSwitchCase="'platforms'" [class.cols-1]="isRegistryPrintable || (block.platformColumns?.length || 0) <= 1" [class.cols-2]="!isRegistryPrintable && (block.platformColumns?.length || 0) === 2" [class.cols-3]="!isRegistryPrintable && (block.platformColumns?.length || 0) >= 3">
            <div class="a4-platform-col" *ngFor="let column of block.platformColumns">
                <ng-container *ngFor="let platform of column">
                    <ng-container *ngTemplateOutlet="platformCardTpl; context: { $implicit: platform }"></ng-container>
                </ng-container>
            </div>
        </div>
    </ng-container>
</ng-template>

<ng-template #platformCardTpl let-platform>
    <!-- GST: same registry layout as the web result page -->
    <article class="result-card source-card gst-registry-card a4-platform-card" *ngIf="isGstPrintable; else dinOrDefaultPlatformCard" [class.is-not-found]="platform?.notFound">
        <header class="gst-reg-header">
            <div class="gst-reg-brand">
                <div class="gst-reg-logo" aria-hidden="true">GST</div>
                <div class="gst-reg-brand-text">
                    <h3 class="gst-reg-title">Company registry &amp; contacts</h3>
                    <p class="gst-reg-subtitle">GST · Gst</p>
                </div>
            </div>
            <span
                class="gst-reg-status"
                [class.is-active]="!platform?.notFound && isGstStatusActive(platform)"
                [class.is-inactive]="platform?.notFound || !isGstStatusActive(platform)"
            >
                <span class="gst-reg-status-dot" aria-hidden="true"></span>
                {{ platform?.notFound ? 'Not found' : getGstStatusLabel(platform) }}
            </span>
        </header>

        <p class="gst-reg-empty" *ngIf="platform?.notFound">No registry data found for this GSTIN.</p>

        <ng-container *ngIf="!platform?.notFound">
            <section class="gst-reg-identity">
                <div class="gst-reg-identity-main">
                    <span class="gst-reg-label">Legal name</span>
                    <h2 class="gst-reg-legal-name">{{ getGstLegalName(platform) }}</h2>
                    <span class="gst-reg-entity-badge" *ngIf="getGstEntityBadge(platform) as badge">{{ badge }}</span>
                </div>
                <div class="gst-reg-dates">
                    <div class="gst-reg-date" *ngIf="getGstValue(platform, 'registration_date') as regDate">
                        <span class="gst-reg-label">Registered</span>
                        <span class="gst-reg-date-value">{{ regDate }}</span>
                    </div>
                    <div class="gst-reg-date" *ngIf="getGstValue(platform, 'last_update_date') as updDate">
                        <span class="gst-reg-label">Last update</span>
                        <span class="gst-reg-date-value">{{ updDate }}</span>
                    </div>
                </div>
            </section>

            <section class="gst-reg-contacts">
                <div class="gst-reg-cell" *ngIf="getGstValue(platform, 'email') as email">
                    <span class="gst-reg-label">Email</span>
                    <span class="gst-reg-value">{{ email }}</span>
                </div>
                <div class="gst-reg-cell" *ngIf="getGstValue(platform, 'phone', 'mobile') as phone">
                    <span class="gst-reg-label">Phone</span>
                    <span class="gst-reg-value">{{ phone }}</span>
                </div>
                <div class="gst-reg-cell" *ngIf="getGstValue(platform, 'gstin') as gstin">
                    <span class="gst-reg-label">GSTIN</span>
                    <span class="gst-reg-value">{{ gstin }}</span>
                </div>
                <div class="gst-reg-cell" *ngIf="getGstValue(platform, 'address', 'principal_place_address') as address">
                    <span class="gst-reg-label">Address</span>
                    <span class="gst-reg-value">{{ address }}</span>
                </div>
            </section>

            <section class="gst-reg-jurisdictions">
                <div class="gst-reg-cell" *ngIf="getGstValue(platform, 'state_jurisdiction') as stateJ">
                    <span class="gst-reg-label">State jurisdiction</span>
                    <span class="gst-reg-value">{{ stateJ }}</span>
                </div>
                <div class="gst-reg-cell" *ngIf="getGstValue(platform, 'center_jurisdiction') as centerJ">
                    <span class="gst-reg-label">Center jurisdiction</span>
                    <span class="gst-reg-value">{{ centerJ }}</span>
                </div>
                <div class="gst-reg-cell" *ngIf="getGstNatureTags(platform) as tags">
                    <ng-container *ngIf="tags.length">
                        <span class="gst-reg-label">Nature of business</span>
                        <div class="gst-reg-tags">
                            <span class="gst-reg-tag" *ngFor="let tag of tags">{{ tag }}</span>
                        </div>
                    </ng-container>
                </div>
            </section>
        </ng-container>
    </article>

    <ng-template #dinOrDefaultPlatformCard>
    <article class="result-card source-card din-registry-card a4-platform-card" *ngIf="isDinPrintable; else defaultPlatformCard" [class.is-not-found]="platform?.notFound">
        <header class="din-reg-header">
            <div class="din-reg-brand">
                <div class="din-reg-logo" aria-hidden="true">DIN</div>
                <div class="din-reg-brand-text">
                    <h3 class="din-reg-title">Director registry &amp; contacts</h3>
                    <p class="din-reg-subtitle">DIN · ZaubaCorp</p>
                </div>
            </div>
            <span
                class="din-reg-status"
                [class.is-active]="!platform?.notFound && isDinStatusActive(platform)"
                [class.is-inactive]="platform?.notFound || !isDinStatusActive(platform)"
            >
                <span class="din-reg-status-dot" aria-hidden="true"></span>
                {{ platform?.notFound ? 'Not found' : getDinStatusLabel(platform) }}
            </span>
        </header>

        <p class="din-reg-empty" *ngIf="platform?.notFound">No registry data found for this DIN.</p>

        <ng-container *ngIf="!platform?.notFound">
            <section class="din-reg-identity">
                <div class="din-reg-identity-main">
                    <span class="din-reg-label">Director name</span>
                    <h2 class="din-reg-legal-name">{{ getDinDirectorName(platform) }}</h2>
                    <div class="din-reg-chips" *ngIf="getDinCompanyCount(platform) || getDinCurrentCompanies(platform).length">
                        <span class="din-reg-entity-badge" *ngIf="getDinCompanyCount(platform) as count">{{ count }} companies</span>
                        <span class="din-reg-tag" *ngFor="let co of getDinCurrentCompanies(platform)">{{ co }}</span>
                    </div>
                </div>
                <div class="din-reg-dates" *ngIf="getDinValue(platform, 'registry_source') as source">
                    <div class="din-reg-date">
                        <span class="din-reg-label">Registry</span>
                        <span class="din-reg-date-value">{{ source }}</span>
                    </div>
                </div>
            </section>

            <section class="din-reg-contacts">
                <div class="din-reg-cell" *ngIf="getDinValue(platform, 'din') as dinVal">
                    <span class="din-reg-label">DIN</span>
                    <span class="din-reg-value">{{ dinVal }}</span>
                </div>
                <div class="din-reg-cell" *ngIf="getDinValue(platform, 'email') as email">
                    <span class="din-reg-label">Email</span>
                    <span class="din-reg-value">{{ email }}</span>
                </div>
                <div class="din-reg-cell" *ngIf="getDinValue(platform, 'phone', 'mobile') as phone">
                    <span class="din-reg-label">Phone</span>
                    <span class="din-reg-value">{{ phone }}</span>
                </div>
                <div class="din-reg-cell din-reg-cell-wide" *ngIf="getDinValue(platform, 'profile_url') as profileUrl">
                    <span class="din-reg-label">Profile</span>
                    <span class="din-reg-value">{{ getLinkDisplayText(profileUrl) }}</span>
                </div>
                <div class="din-reg-cell din-reg-cell-wide" *ngIf="getDinValue(platform, 'about') as about">
                    <span class="din-reg-label">About</span>
                    <span class="din-reg-value">{{ about }}</span>
                </div>
                <div class="din-reg-cell din-reg-cell-wide" *ngIf="getDinValue(platform, 'address', 'registered_address') as address">
                    <span class="din-reg-label">Address</span>
                    <span class="din-reg-value">{{ address }}</span>
                </div>
                <div class="din-reg-cell din-reg-cell-wide" *ngIf="getDinRegistrySources(platform) as sources">
                    <ng-container *ngIf="sources.length">
                        <span class="din-reg-label">Registry sources</span>
                        <div class="din-reg-tags">
                            <span class="din-reg-tag" *ngFor="let src of sources">{{ src }}</span>
                        </div>
                    </ng-container>
                </div>
            </section>

            <section class="din-reg-companies" *ngIf="getDinCompanies(platform) as companies">
                <ng-container *ngIf="companies.length">
                    <h4 class="din-reg-section-title">Companies associated</h4>
                    <div class="din-reg-table-wrap">
                        <table class="din-reg-table">
                            <thead>
                                <tr>
                                    <th>Company</th>
                                    <th>Designation</th>
                                    <th>Date of appointment</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let row of companies">
                                    <td>{{ row.company || '—' }}</td>
                                    <td>{{ row.designation || '—' }}</td>
                                    <td>{{ row.date_of_appointment || '—' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ng-container>
            </section>

            <section class="din-reg-timeline" *ngIf="getDinTimeline(platform) as timeline">
                <ng-container *ngIf="timeline.length">
                    <h4 class="din-reg-section-title">Timeline</h4>
                    <div class="din-reg-table-wrap">
                        <table class="din-reg-table">
                            <thead>
                                <tr>
                                    <th>Company</th>
                                    <th>Role</th>
                                    <th>Type</th>
                                    <th>Period</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let row of timeline">
                                    <td>{{ row.company || '—' }}</td>
                                    <td>{{ row.role || '—' }}</td>
                                    <td>{{ row.type || '—' }}</td>
                                    <td>{{ formatDinPeriod(row.start, row.end) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ng-container>
            </section>
        </ng-container>
    </article>
    </ng-template>

    <ng-template #defaultPlatformCard>
    <article class="result-card source-card abacus-card a4-platform-card" [class.data-dense]="isDataDenseSourceCard(platform)" [class.is-not-found]="platform?.notFound" [class.is-unknown]="platform?.isUnknown" [ngStyle]="getPlatformThemeStyle(platform?.platformKey || platform?.platform)">
        <header class="abacus-card-header">
            <div class="abacus-card-identity">
                <div class="abacus-icon-wrap">
                    <img [src]="platform?.icon" [alt]="platform?.platform" class="abacus-platform-icon" (error)="onIconError($event)" />
                </div>
                <div class="abacus-title-wrap">
                    <h3 class="abacus-platform-name">{{ platform?.platform }}</h3>
                    <p class="abacus-platform-category">{{ platform?.category || getPlatformCategory(platform?.platform) }}</p>
                </div>
            </div>
            <div class="abacus-card-actions">
                <span class="abacus-status-badge" [class.is-found]="!platform?.notFound && !platform?.isUnknown" [class.is-missing]="platform?.notFound" [class.is-unknown]="platform?.isUnknown">
                    <i class="bi" [ngClass]="platform?.isUnknown ? 'bi-question-circle' : (platform?.notFound ? 'bi-x-circle' : 'bi-check-circle')" aria-hidden="true"></i>
                </span>
                <a *ngIf="getProfileLinkDetail(platform) as headerProfileLink" class="abacus-header-external-link" [href]="utilsService.toLink(headerProfileLink.value)" target="_blank" rel="noopener noreferrer" [attr.title]="headerProfileLink.value">
                    <i class="fas fa-external-link-alt" aria-hidden="true"></i>
                </a>
            </div>
        </header>

        <p class="abacus-not-found-message" *ngIf="platform?.notFound">Not found</p>
        <p class="abacus-not-found-message is-unknown" *ngIf="platform?.isUnknown">Could not verify</p>

        <div class="abacus-card-body" *ngIf="!platform?.notFound && !platform?.isUnknown">
            <div class="abacus-field abacus-pictures-field" *ngIf="getPlatformImageForCard(platform) as platformImage">
                <span class="abacus-field-label">Pictures</span>
                <div class="abacus-picture-frame">
                    <img [src]="platformImage.src" [alt]="platformImage.name" class="abacus-picture" (error)="onIconError($event)" />
                </div>
            </div>

            <p class="abacus-description" *ngIf="getCardDescription(platform) as description">
                {{ description }}
            </p>

            <div class="abacus-field" *ngFor="let detail of getCardBodyDetails(platform)">
                <span class="abacus-field-label">{{ detail.label }}</span>
                <div class="abacus-field-value-row">
                    <ng-container [ngSwitch]="detail.type">
                        <a *ngSwitchCase="'link'" [href]="utilsService.toLink(detail.value)" target="_blank" rel="noopener noreferrer" class="abacus-field-value abacus-inline-link">{{ detail.value }}</a>
                        <span *ngSwitchCase="'datetime'" class="abacus-field-value">{{ utilsService.formatDateTime(detail.value) }}</span>
                        <span *ngSwitchCase="'date'" class="abacus-field-value">{{ formatDateValue(detail.value) }}</span>
                        <span *ngSwitchCase="'boolean'" class="abacus-field-value">{{ detail.value ? 'Yes' : 'No' }}</span>
                        <span *ngSwitchCase="'number'" class="abacus-field-value">{{ detail.value }}</span>
                        <ng-container *ngSwitchCase="'list'">
                            <span *ngIf="isEmptyValue(detail.value)" class="abacus-field-value">N/A</span>
                            <ng-container *ngIf="!isEmptyValue(detail.value) && getDisplayListItems(detail) as listItems">
                                <span *ngIf="listItems.length <= 1" class="abacus-field-value is-structured">
                                    <app-abacus-structured-value [text]="listItems[0]"></app-abacus-structured-value>
                                </span>
                                <ul *ngIf="listItems.length > 1" class="abacus-field-value abacus-field-list is-structured" [class.abacus-address-bullets]="utilsService.usesDiscBullets(detail)" [class.abacus-datetime-bullets]="utilsService.isDateTimeBulletList(detail)" [class.abacus-plain-bullets]="utilsService.isPlainStringList(detail)">
                                    <li *ngFor="let item of listItems">
                                        <app-abacus-structured-value [text]="item"></app-abacus-structured-value>
                                    </li>
                                </ul>
                            </ng-container>
                        </ng-container>
                        <ng-container *ngSwitchCase="'array'">
                            <span *ngIf="isEmptyValue(detail.value)" class="abacus-field-value">N/A</span>
                            <ng-container *ngIf="!isEmptyValue(detail.value) && getDisplayListItems(detail) as listItems">
                                <span *ngIf="listItems.length <= 1" class="abacus-field-value is-structured">
                                    <app-abacus-structured-value [text]="listItems[0]"></app-abacus-structured-value>
                                </span>
                                <ul *ngIf="listItems.length > 1" class="abacus-field-value abacus-field-list is-structured" [class.abacus-address-bullets]="utilsService.usesDiscBullets(detail)" [class.abacus-datetime-bullets]="utilsService.isDateTimeBulletList(detail)" [class.abacus-plain-bullets]="utilsService.isPlainStringList(detail)">
                                    <li *ngFor="let item of listItems">
                                        <app-abacus-structured-value [text]="item"></app-abacus-structured-value>
                                    </li>
                                </ul>
                            </ng-container>
                        </ng-container>
                        <span *ngSwitchCase="'object'" class="abacus-field-value is-structured">
                            <app-abacus-structured-value [text]="isEmptyValue(detail.value) ? '{}' : formatObjectValue(detail.value)"></app-abacus-structured-value>
                        </span>
                        <span *ngSwitchCase="'dict'" class="abacus-field-value is-structured">
                            <app-abacus-structured-value [text]="isEmptyValue(detail.value) ? 'N/A' : formatObjectValue(detail.value)"></app-abacus-structured-value>
                        </span>
                        <span *ngSwitchDefault class="abacus-field-value" [class.is-structured]="hasStructuredFieldValue(detail)" [class.is-active]="isActiveStatusValue(detail)">
                            <app-abacus-structured-value *ngIf="hasStructuredFieldValue(detail); else plainPrintableValue" [text]="utilsService.formatDisplayValue(detail.value)"></app-abacus-structured-value>
                            <ng-template #plainPrintableValue>{{ utilsService.formatDisplayValue(detail.value) }}</ng-template>
                        </span>
                    </ng-container>
                </div>
            </div>
        </div>

        <a *ngIf="getProfileLinkDetail(platform) as profileLink" class="abacus-external-link" [href]="utilsService.toLink(profileLink.value)" target="_blank" rel="noopener noreferrer" [attr.title]="profileLink.value">
            <i class="fas fa-external-link-alt" aria-hidden="true"></i>
            <span>{{ getLinkDisplayText(profileLink.value) }}</span>
        </a>
    </article>
    </ng-template>
</ng-template>